/**
 * QuickPay — Iter 219
 * -------------------
 * Public no-auth page at `/quickpay`. Anyone can:
 *   1. Look up a customer by phone or customer code.
 *   2. See total outstanding.
 *   3. Pay via Razorpay (extensible to PhonePe / Cashfree later).
 *   4. Get instant confirmation — the existing bundle-payment webhook
 *      + verify pipeline flips every invoice to `paid`.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const PREFIX   = "/api/payments/razorpay";  // matches payment_routes.py

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function QuickPay() {
  const [q, setQ]                 = useState("");
  const [searching, setSearching] = useState(false);
  const [matches, setMatches]     = useState([]);
  const [gateways, setGateways]   = useState([]);
  const [picked, setPicked]       = useState(null);
  const [payingWith, setPayingWith] = useState(null);
  const [result, setResult]       = useState(null);  // {status: "paid"|"failed", ...}

  useEffect(() => {
    axios.get(`${API_BASE}${PREFIX}/public/quickpay/gateways`)
      .then(r => setGateways(r.data?.providers || []))
      .catch(() => setGateways([]));
  }, []);

  const activeProviders = useMemo(
    () => gateways.filter(g => g.enabled),
    [gateways],
  );

  const search = async (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (query.length < 6) { toast.error("Enter your full 10-digit phone number or full customer code"); return; }
    setSearching(true);
    setPicked(null);
    setMatches([]);
    setResult(null);
    try {
      const r = await axios.get(
        `${API_BASE}${PREFIX}/public/quickpay/lookup`,
        { params: { q: query } },
      );
      setMatches(r.data?.matches || []);
      if ((r.data?.matches || []).length === 0) {
        toast.info("No customer found for that search");
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Search failed");
    } finally { setSearching(false); }
  };

  const pay = async (provider) => {
    if (!picked) return;
    if (picked.due <= 0) { toast.info("Nothing due — you're all clear!"); return; }
    setPayingWith(provider);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Payment SDK failed to load. Check your connection.");
      const or = await axios.post(
        `${API_BASE}${PREFIX}/public/quickpay/order`,
        { customer_id: picked.customer_id, provider },
      );
      const order = or.data;
      const rz = new window.Razorpay({
        key:      order.key_id,
        amount:   order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name:     "Rescigo · QuickPay",
        description: `Settle ${order.invoice_no}`,
        prefill:  order.prefill,
        notes:    order.notes,
        theme:    { color: "#DA1616" },
        handler:  async (rsp) => {
          try {
            await axios.post(`${API_BASE}${PREFIX}/public-verify`, {
              razorpay_order_id:   rsp.razorpay_order_id,
              razorpay_payment_id: rsp.razorpay_payment_id,
              razorpay_signature:  rsp.razorpay_signature,
            });
            setResult({
              status: "paid",
              amount: (order.amount / 100).toFixed(2),
              count:  order.invoice_ids.length,
              payment_id: rsp.razorpay_payment_id,
            });
          } catch (ve) {
            setResult({ status: "failed",
                          error: ve?.response?.data?.detail || "Verification failed" });
          }
        },
        modal: { ondismiss: () => setPayingWith(null) },
      });
      rz.open();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || e2.message || "Payment failed");
      setPayingWith(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3EC] text-[#1A1A1A] px-4 py-6"
          data-testid="quickpay-page">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-lg bg-[#DA1616] text-white
                          flex items-center justify-center font-heading text-2xl">R</div>
          <div>
            <div className="font-heading text-2xl leading-none">QuickPay</div>
            <div className="text-[12px] text-[#5C5A55] mt-1">
              Rescigo bills · pay anytime, no login needed
            </div>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={search}
               className="bg-white border border-[#E5E2D9] rounded-lg p-3 mb-4"
               data-testid="quickpay-search-card">
          <label className="text-[10px] uppercase tracking-[0.16em] text-[#5C5A55]">
            Phone number or customer code
          </label>
          <div className="flex gap-2 mt-1.5">
            <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="e.g. 9447739059 or XX26G0889"
                    inputMode="text" autoFocus
                    data-testid="quickpay-search-input"
                    className="flex-1 h-11 px-3 border border-[#E5E2D9] rounded text-sm"/>
            <button type="submit" disabled={searching}
                     data-testid="quickpay-search-btn"
                     className="h-11 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded text-sm font-medium">
              {searching ? "…" : "Search"}
            </button>
          </div>
        </form>

        {/* Match list */}
        {matches.length > 0 && !picked && (
          <div className="space-y-2 mb-4" data-testid="quickpay-matches">
            {matches.map(m => (
              <button key={m.customer_id} onClick={() => setPicked(m)}
                       data-testid={`quickpay-match-${m.customer_id}`}
                       className="w-full text-left bg-white border border-[#E5E2D9] hover:border-[#2D5A27] rounded-lg p-3">
                <div className="flex items-baseline justify-between">
                  <div className="font-semibold truncate">{m.name || m.customer_code || m.phone}</div>
                  <div className={`text-sm font-mono ${m.due > 0 ? "text-[#DA1616]" : "text-[#2D5A27]"}`}>
                    ₹{m.due.toFixed(2)}
                  </div>
                </div>
                <div className="text-[11px] text-[#5C5A55] mt-1 truncate">
                  {m.phone} · {m.customer_code} · {m.unpaid_count} unpaid
                </div>
                {m.tenant_name && (
                  <div className="text-[10px] text-[#8A8780] mt-0.5 truncate">
                    {m.tenant_name}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Picked customer + pay */}
        {picked && !result && (
          <div className="bg-white border border-[#E5E2D9] rounded-lg p-4 space-y-4"
                data-testid="quickpay-picked-card">
            <div>
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-heading text-lg truncate">
                  {picked.name || picked.customer_code}
                </div>
                <button onClick={() => setPicked(null)}
                         data-testid="quickpay-change"
                         className="text-[11px] text-[#2D5A27] hover:underline shrink-0">
                  Change
                </button>
              </div>
              <div className="text-[12px] text-[#5C5A55] truncate">
                {picked.phone} · {picked.customer_code}
              </div>
            </div>

            <div className="bg-[#F5F3EC] rounded-md p-3 text-center">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">Total due</div>
              <div className="font-heading text-4xl mt-1"
                    data-testid="quickpay-due">
                ₹{picked.due.toFixed(2)}
              </div>
              <div className="text-[11px] text-[#5C5A55] mt-1">
                across {picked.unpaid_count} invoice{picked.unpaid_count === 1 ? "" : "s"}
              </div>
            </div>

            {picked.invoices?.length > 0 && (
              <details className="bg-[#FAFAF7] rounded-md">
                <summary className="cursor-pointer text-[11px] uppercase tracking-[0.14em] text-[#5C5A55] px-3 py-2">
                  View invoices ({picked.unpaid_count})
                </summary>
                <div className="divide-y divide-[#F0EDE4]">
                  {picked.invoices.map(inv => (
                    <div key={inv.id} className="flex justify-between px-3 py-2 text-xs">
                      <span>{inv.invoice_no}</span>
                      <span className="font-mono">₹{Number(inv.total).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {picked.due > 0 ? (
              <div className="space-y-2" data-testid="quickpay-gateways">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55]">
                  Choose payment method
                </div>
                {gateways.map(g => (
                  <button key={g.key} onClick={() => g.enabled && pay(g.key)}
                           disabled={!g.enabled || payingWith === g.key}
                           data-testid={`quickpay-pay-${g.key}`}
                           className={`w-full h-12 flex items-center gap-3 px-3 rounded-md border ${
                             g.enabled
                               ? "border-[#2D5A27] hover:bg-[#EDF7EA]"
                               : "border-[#E5E2D9] opacity-50 cursor-not-allowed"
                           }`}>
                    <span className="text-sm font-medium flex-1 text-left">{g.name}</span>
                    <span className="text-[11px] text-[#5C5A55]">
                      {payingWith === g.key ? "Opening…"
                        : g.enabled ? "→"
                        : (g.note || "Disabled")}
                    </span>
                  </button>
                ))}
                {activeProviders.length === 0 && (
                  <div className="bg-[#FDF3D0] border border-[#EFDCA1] rounded-md p-3 text-xs text-[#7A5A11]">
                    No online payment gateway is active for this tenant right
                    now. Please contact support.
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-[#EDF7EA] border border-[#B7D3AF] rounded-md p-3 text-center text-sm text-[#1F3A19] font-medium">
                ✓ You&rsquo;re all clear — nothing due.
              </div>
            )}
          </div>
        )}

        {/* Result */}
        {result && (
          <div data-testid="quickpay-result"
                className={`rounded-lg p-6 text-center space-y-3 ${
                  result.status === "paid"
                    ? "bg-[#EDF7EA] border border-[#B7D3AF]"
                    : "bg-[#F9EDE7] border border-[#EFBCA3]"
                }`}>
            {result.status === "paid" ? (
              <>
                <div className="text-5xl">✓</div>
                <div className="font-heading text-2xl">Payment received</div>
                <div className="text-sm text-[#1F3A19]">
                  ₹{result.amount} for {result.count} invoice{result.count === 1 ? "" : "s"}.
                </div>
                <div className="text-[11px] font-mono text-[#5C5A55]">
                  Ref: {result.payment_id}
                </div>
                <button onClick={() => { setResult(null); setPicked(null); setMatches([]); setQ(""); }}
                         data-testid="quickpay-done"
                         className="mt-2 h-10 px-6 bg-[#2D5A27] text-white rounded text-sm font-medium">
                  Done
                </button>
              </>
            ) : (
              <>
                <div className="text-5xl">✕</div>
                <div className="font-heading text-2xl">Payment failed</div>
                <div className="text-sm text-[#7A2E18]">{result.error}</div>
                <button onClick={() => setResult(null)} data-testid="quickpay-retry"
                         className="mt-2 h-10 px-6 bg-[#DA1616] text-white rounded text-sm font-medium">
                  Try again
                </button>
              </>
            )}
          </div>
        )}

        <div className="text-[10px] text-[#8A8780] text-center mt-6">
          Powered by Rescigo · Secure payments by Razorpay
        </div>
      </div>
    </div>
  );
}
